/**
 * Clustering and sorting rows by event kind.
 *
 * No imports.
 */

/** Standard event kinds in display order. `market` sits last, after talks
 *  and tours — the same order `entries.ts`'s `LANES` puts its "Talks and
 *  tours" lane ahead of "Markets and food". It has to be named here at all:
 *  a market row's real `type` is literally `'market'` (`browse.ts`'s
 *  `kindOf` reads that), so the default `kindOf` below — `row => row.type`
 *  — hands it straight to `KINDS.indexOf` with nothing else in between. */
export const KINDS = [
  'concert',
  'film',
  'exhibition',
  'opera',
  'theatre',
  // Blind spots A2: `browse.ts`'s `kindOf` answers these now, and a section
  // mixing them puts them where their lanes sit — after Stage, before Talks.
  'club',
  'gig',
  // Nightlife N6: `browse.ts`'s `kindOf` answers `quiz` too, and a mixed
  // section puts one after the club nights and gigs, where its chip sits.
  'quiz',
  'talk',
  'tour',
  'market'
] as const

export type Kind = typeof KINDS[number]

/**
 * A kind's sort position, an unrecognised one sorting after every kind
 * `KINDS` names rather than `Array.indexOf`'s `-1` — which, being less than
 * every real index, would otherwise put whatever this file has never heard
 * of first in a mixed section. `market` is named in `KINDS` now, but a
 * caller's own `kindOf` can still hand back anything (`test('custom kindOf
 * function', ...)` passes `'movie'`), so this stays the one place that
 * decides what "unknown" means instead of leaving it to the accident of
 * where `indexOf` fails.
 */
function rankOf(kind: string): number {
  const i = (KINDS as readonly string[]).indexOf(kind)
  return i === -1 ? KINDS.length : i
}

interface RowForKind {
  type: string
  film?: { rt_score?: number | null; metascore?: number | null; imdb_rating?: number | null } | null
  next_showing?: string | null
}

interface ClusterResult {
  kind: string
  rows: RowForKind[]
  hidden: number
}

/**
 * Group rows by kind, maintaining KINDS order and optionally capping display.
 *
 * Returns array of { kind, rows, hidden } where rows is the shown portion
 * and hidden is how many were omitted by the cap. If cap is 0 (default),
 * all rows are shown.
 */
export function clusterByKind(
  rows: RowForKind[],
  opts: { cap?: number; kindOf?: (row: RowForKind) => string } = {}
): ClusterResult[] {
  const cap = opts.cap || 0
  const kindOf = opts.kindOf || ((row: RowForKind) => row.type)

  // Group by kind
  const groups = new Map<string, RowForKind[]>()
  for (const row of rows) {
    const kind = kindOf(row)
    if (!groups.has(kind)) groups.set(kind, [])
    groups.get(kind)!.push(row)
  }

  // Sort groups by KINDS order
  const sorted = [...groups.entries()].sort(
    ([a], [b]) => rankOf(a) - rankOf(b)
  )

  // Build result with capping
  const result: ClusterResult[] = []
  for (const [kind, all] of sorted) {
    // For films with a cap, sort by score then next_showing
    let toShow = all
    if (cap && kind === 'film') {
      toShow = [...all].sort((a, b) => {
        const aHasScore = !!(a.film?.rt_score || a.film?.metascore || a.film?.imdb_rating)
        const bHasScore = !!(b.film?.rt_score || b.film?.metascore || b.film?.imdb_rating)
        if (aHasScore !== bHasScore) return bHasScore ? 1 : -1
        return (a.next_showing || '') < (b.next_showing || '') ? -1 : 1
      })
      toShow = toShow.slice(0, cap)
    }

    result.push({
      kind,
      rows: toShow,
      hidden: all.length - toShow.length
    })
  }

  return result
}

/**
 * Sort films so scored ones come first, maintaining order within each group.
 *
 * Scored films (with rt, metascore, or imdb rating) come before unscored,
 * then sorted by next_showing within each group.
 */
export function filmsWithScoresFirst(rows: RowForKind[]): RowForKind[] {
  return [...rows].sort((a, b) => {
    const aHasScore = !!(a.film?.rt_score || a.film?.metascore || a.film?.imdb_rating)
    const bHasScore = !!(b.film?.rt_score || b.film?.metascore || b.film?.imdb_rating)
    if (aHasScore !== bHasScore) return bHasScore ? 1 : -1
    return (a.next_showing || '') < (b.next_showing || '') ? -1 : 1
  })
}

/**
 * Kind groups for a list that groups only when it is mixed: `clusterByKind`'s
 * groups when the rows hold more than one kind, `null` when they hold one (or
 * none), which the caller draws flat with no heading.
 *
 * UI audit a1-24, Doug's choice of 2 Oct: every day block on Today — the week
 * ahead's days and a day picked in the strip — follows this one rule. It
 * used to be two: a week-ahead day grouped only at the Thursday/Friday
 * weekend cap, so one day drew flat beside the next drawn in kind groups, the
 * shape changing with the count rather than with what was in it. A heading
 * over a single kind repeats what every row's own kind word already says,
 * which is why one kind stays flat.
 */
export function groupsWhenMixed(
  rows: RowForKind[],
  opts: { kindOf?: (row: RowForKind) => string } = {}
): ClusterResult[] | null {
  const groups = clusterByKind(rows, { kindOf: opts.kindOf })
  return groups.length > 1 ? groups : null
}
